import { defineEntityItemDerivation } from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildCubeShape, buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';
import type { EntityItemDerivation } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toNodeItemKey } from '@/adapters/core/_pure/keyCodec';
import type { Config } from '@/adapters/core/_contract/config';
import type { VisualNode } from '@/adapters/core/_pure/projection';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { CoreChangeHighlight } from './agentChanges';

function toNodeRadius(node: VisualNode, config: Config): number {
    if (node.parentPath === null) return config.geometry.rootRadius;
    return node.nodeKind === 'directory' ? config.geometry.directoryRadius : config.geometry.fileRadius;
}

function toRenderedNodeRadius(node: VisualNode, config: Config): number {
    return toNodeRadius(node, config) * config.geometry.renderRadiusMultiplier;
}

function toNodeColor(node: VisualNode, config: Config): string {
    if (node.parentPath === null) return config.visualStyle.rootColor;
    if (node.boundaryKind !== null) return config.visualStyle.boundaryColors[node.boundaryKind];
    if (node.nodeKind === 'directory') return config.visualStyle.directoryColor;
    return config.visualStyle.fileColor;
}

function toNodeLabelFontSize(node: VisualNode, radius: number): number {
    if (node.parentPath === null) return Math.max(3.2, radius * 0.5);
    if (node.depth <= 2) return Math.max(2.4, radius * 0.6);
    if (node.depth <= 4) return Math.max(1.8, radius * 0.5);
    if (node.nodeKind === 'directory') return Math.max(1.5, radius * 0.48);
    return Math.max(0.9, radius * 0.3);
}

export function toNodeItemDerivation(node: VisualNode, config: Config, radiusOverride?: number, position?: Vector3Tuple, highlight?: CoreChangeHighlight): EntityItemDerivation {
    const isDirectory = node.nodeKind === 'directory';
    const isBoundaryDir = isDirectory && node.boundaryKind !== null;
    const labelVisible = !!highlight || config.showLabelsAtAllLevels || radiusOverride !== undefined || node.parentPath === null
        || (isDirectory && node.depth <= 6)
        || (isBoundaryDir && node.depth <= 8);
    const radius = radiusOverride ?? toRenderedNodeRadius(node, config);
    const color = highlight ? highlight.planned ? highlight.direct ? '#b394ff' : '#ef91cc'
        : highlight.direct ? '#40e0ed' : '#f0b030' : undefined;
    const highlightDescription = highlight?.planned
        ? highlight.direct ? 'Declared planned file (intention, not an edit)' : `${highlight.count} planned file(s) under this folder; files new, absent or hidden`
        : highlight?.direct ? 'Selected changeset file' : `${highlight?.count} selected change(s) under this folder; files absent or hidden`;

    return defineEntityItemDerivation({
        item: {
            itemKind: 'entity',
            itemKey: toNodeItemKey(node.path),
            name: node.name,
            description: highlight ? `${node.path}\n${highlightDescription}` : node.path,
        },
        parametricState: {
            ...(position ? { position } : {}),
            shape: isDirectory
                ? buildSphereShape({ radius })
                : buildCubeShape({ side: 2 * radius / Math.sqrt(3) }),
            visualStyle: {
                color: color ?? toNodeColor(node, config),
                opacity: highlight ? 1 : config.visualStyle.inactiveOpacity,
            },
            label: labelVisible
                ? {
                    aboveItem: {
                        text: `${node.parentPath === null ? 'Core' : node.name}${highlight ? highlight.planned
                            ? highlight.direct ? ' [planned]' : ` [${highlight.count} planned below]`
                            : highlight.direct ? ' *' : ` [${highlight.count} below]` : ''}`,
                        color: color ?? config.visualStyle.labelColor,
                        fontSize: radiusOverride === undefined ? toNodeLabelFontSize(node, radius) : Math.max(0.3, radius * 0.7),
                        orientation: { orientationKind: 'billboard' },
                    },
                }
                : {},
        },
    });
}